import moment, { Moment } from 'moment-timezone';
import { AxiosInstance } from 'axios';

import {
  CoreSpaceCountBucket,
  CoreSpaceEvent,
  CoreWebsocketCountChangePayload,
  CoreWebsocketCountResetPayload,
  CoreWebsocketDisplayRefreshPayload,
  CoreSpace,
} from '@densityco/lib-api-types';
import { fetchAllObjects, handleVisibilityChange, asyncSleep } from '@densityco/lib-common-helpers';
import { getLastDailyReset } from '../daily-reset';

// Every 60 seconds, send a ping on the websocket connection. This is to ensure that it stays open.
const WEBSOCKET_PING_MESSAGE_INTERVAL_IN_MILLISECONDS = 60 * 1000;

export enum LiveCountStatus {
  CLOSED = 'CLOSED',
  WAITING_FOR_SOCKET_URL = 'WAITING_FOR_SOCKET_URL',
  CONNECTING = 'CONNECTING',
  CONNECTED = 'CONNECTED',
  ERROR = 'ERROR',
}

export enum AnalyticsTime {
  TODAY = 'TODAY',
  FIFTEEN_MINUTES = 'FIFTEEN_MINUTES',
  THIRTY_MINUTES = 'THIRTY_MINUTES',
  ONE_HOUR = 'ONE_HOUR',
  THREE_HOURS = 'THREE_HOURS',
  SIX_HOURS = 'SIX_HOURS',
  TWELVE_HOURS = 'TWELVE_HOURS',
}

export type StartEndTimeData = {
  start_time: string;
  end_time: string;
};

export type AnalyticsTimeDataMap = {
  [key in AnalyticsTime]: StartEndTimeData;
};

export type LiveCountPayload =
  | CoreWebsocketCountChangePayload
  | CoreWebsocketCountResetPayload
  | CoreWebsocketDisplayRefreshPayload;

export type LiveCountUpdate =
  | {
      type: 'count';
      timestamp: string;
      count: number;
      countChange: number;
      doorwayId: string;
    }
  | {
      type: 'count_reset';
      timestamp: string;
      count: number;
    };
export type LiveCountData = {
  currentCount: number;
  latestDelta: number;
  latestReset: number;
  recentEvents: Map<string, LiveCountUpdate>;
  todayAnalytics: {
    // backward compatibility
    entrances: number;
    exits: number;
    max: number;
  };
  analytics: {
    max: number;
    entrances: number;
    exits: number;
  };
};

export type LiveCountChangeHandler = (
  status: LiveCountStatus,
  data: Map<string, LiveCountData>,
  payload: LiveCountPayload | null
) => void;
export type LiveCountProvider = {
  status: LiveCountStatus;
  lastUpdate: number;
  data: ReadonlyMap<string, LiveCountData>;
  socket: WebSocket | null;
  connect: (
    onChange: LiveCountChangeHandler,
    client: AxiosInstance,
    spaceIds?: Array<string>,
    doorwayId?: string,
    pollEvery?: number,
    keepEventsFor?: number,
    analyticsTime?: AnalyticsTime
  ) => void;
  disconnect: () => void;
};

async function getSpaceData(
  client: AxiosInstance,
  space: CoreSpace,
  doorwayId: string | null,
  keepEventsFor: number | null,
  analyticsTime?: AnalyticsTime
) {
  const spaceData: LiveCountData = {
    currentCount: space.current_count,
    latestDelta: moment.min().valueOf(),
    latestReset: moment.min().valueOf(),
    recentEvents: new Map<string, LiveCountUpdate>(),
    todayAnalytics: {
      max: 0,
      entrances: 0,
      exits: 0,
    },
    analytics: {
      max: 0,
      entrances: 0,
      exits: 0,
    },
  };

  // Timestamps for API queries
  const now = moment.tz(space.time_zone);
  const today = now.clone().startOf('day');
  const tomorrow = today.clone().add(1, 'day');
  const fiveSecondsFromNow = now.clone().add(5, 'seconds');
  const fifteenMinutesAgo = now.clone().subtract(15, 'minutes');
  const thirtyMinutesAgo = now.clone().subtract(30, 'minutes');
  const oneHourAgo = now.clone().subtract(1, 'hour');
  const threeHoursAgo = now.clone().subtract(3, 'hours');
  const sixHoursAgo = now.clone().subtract(6, 'hours');
  const twelveHoursAgo = now.clone().subtract(12, 'hours');
  const fetchSince = keepEventsFor ? now.clone().subtract(keepEventsFor, 'milliseconds') : getLastDailyReset(space, now);

  // Define start / end time
  const startEndTime: AnalyticsTimeDataMap = {
    [AnalyticsTime.TODAY]: {
      start_time: today.toISOString(),
      end_time: tomorrow.toISOString(),
    },
    [AnalyticsTime.FIFTEEN_MINUTES]: {
      start_time: fifteenMinutesAgo.toISOString(),
      end_time: now.toISOString(),
    },
    [AnalyticsTime.THIRTY_MINUTES]: {
      start_time: thirtyMinutesAgo.toISOString(),
      end_time: now.toISOString(),
    },
    [AnalyticsTime.ONE_HOUR]: {
      start_time: oneHourAgo.toISOString(),
      end_time: now.toISOString(),
    },
    [AnalyticsTime.THREE_HOURS]: {
      start_time: threeHoursAgo.toISOString(),
      end_time: now.toISOString(),
    },
    [AnalyticsTime.SIX_HOURS]: {
      start_time: sixHoursAgo.toISOString(),
      end_time: now.toISOString(),
    },
    [AnalyticsTime.TWELVE_HOURS]: {
      start_time: twelveHoursAgo.toISOString(),
      end_time: now.toISOString(),
    },
  };

  // Make these API queries in parallel
  const [recentCountResets, recentCountDeltas, todayAnalyticsResponse, analyticsResponse] = await Promise.all([
    fetchAllObjects<{
      id: string;
      space_id: string;
      timestamp: string;
      count: number;
      pre_reset_count: number;
    }>(client, `/spaces/${space.id}/resets`, {
      params: {
        page_size: 5000,
        start_time: fetchSince.toISOString(),
        end_time: fiveSecondsFromNow.toISOString(),
        order: 'DESC',
      },
    }),
    fetchAllObjects<CoreSpaceEvent & { count: number }>(client, `/spaces/${space.id}/events`, {
      params: {
        page_size: 5000,
        start_time: fetchSince.toISOString(),
        end_time: fiveSecondsFromNow.toISOString(),
        doorway_id: doorwayId,
        order: 'ASC',
        count: true,
      },
    }),
    !analyticsTime || analyticsTime === AnalyticsTime.TODAY
      ? client.get(doorwayId ? `/spaces/${space.id}/doorways/${doorwayId}/counts` : `/spaces/${space.id}/counts`, {
          params: {
            start_time: startEndTime[AnalyticsTime.TODAY].start_time,
            end_time: startEndTime[AnalyticsTime.TODAY].end_time,
            interval: `1d`,
          },
        })
      : Promise.resolve(null),
    analyticsTime && analyticsTime !== AnalyticsTime.TODAY
      ? client.get(doorwayId ? `/spaces/${space.id}/doorways/${doorwayId}/counts` : `/spaces/${space.id}/counts`, {
          params: {
            start_time: startEndTime[analyticsTime].start_time,
            end_time: startEndTime[analyticsTime].end_time,
            doorway_id: doorwayId || undefined,
            interval: '1d',
          },
        })
      : Promise.resolve(null),
  ]);

  // All count resets since last "daily reset" (reversed, the API returns results in DESC order)
  // TODO: These are keyed on timestamp, which is necessary until WS & API ids agree
  if (recentCountResets) {
    recentCountResets.reverse();
    recentCountResets.forEach((reset) => {
      const timestamp = moment.utc(reset.timestamp).valueOf();
      if (timestamp >= spaceData.latestReset) {
        spaceData.currentCount = reset.count;
      }
      spaceData.latestReset = Math.max(spaceData.latestReset, timestamp);
      spaceData.recentEvents.set(timestamp.toString(), {
        type: 'count_reset',
        timestamp: reset.timestamp,
        count: reset.count,
      });
    });
  }

  // All count deltas since the last "daily reset"
  // TODO: These are keyed on timestamp, which is necessary until WS & API ids agree
  if (recentCountDeltas) {
    recentCountDeltas.forEach((event) => {
      const timestamp = moment.utc(event.timestamp).valueOf();
      if (timestamp >= spaceData.latestDelta && timestamp > spaceData.latestReset) {
        spaceData.currentCount = event.count;
      }
      spaceData.latestDelta = Math.max(spaceData.latestDelta, timestamp);
      spaceData.recentEvents.set(timestamp.toString(), {
        type: 'count',
        timestamp: event.timestamp,
        count: event.count,
        countChange: event.direction,
        doorwayId: event.doorway_id,
      });
    });
  }

  // Stats for today (legacy field for backward compatibility)
  const todayAnalytics = todayAnalyticsResponse?.data?.results[0] as CoreSpaceCountBucket;
  spaceData.todayAnalytics.entrances = todayAnalytics?.interval.analytics.entrances;
  spaceData.todayAnalytics.exits = todayAnalytics?.interval.analytics.exits;
  spaceData.todayAnalytics.max = todayAnalytics?.interval.analytics.max;

  // Copy stats for today to analytics if AnalyticsTime is TODAY
  let analytics: CoreSpaceCountBucket | undefined;

  const analyticsTimeIsToday = analyticsTime === AnalyticsTime.TODAY;

  analytics = analyticsTimeIsToday ? todayAnalytics : (analyticsResponse?.data?.results[0] as CoreSpaceCountBucket);
  spaceData.analytics.entrances = analyticsTimeIsToday ? spaceData.todayAnalytics.max : analytics?.interval.analytics.max;
  spaceData.analytics.entrances = analyticsTimeIsToday
    ? spaceData.todayAnalytics.entrances
    : analytics?.interval.analytics.entrances;
  spaceData.analytics.exits = analyticsTimeIsToday ? spaceData.todayAnalytics.exits : analytics?.interval.analytics.exits;

  return spaceData;
}

async function getSpaceCount(client: AxiosInstance, space: CoreSpace, time: Moment | null = null) {
  const currentCountResponse = await client.get(`/spaces/${space.id}/count`, {
    params: { time: time ? time.toISOString() : moment.max().toISOString() },
  });
  return currentCountResponse.data?.count as number;
}

async function pollForData(
  client: AxiosInstance,
  spaceIds: string[] | null,
  doorwayId: string | null,
  keepEventsFor: number | null,
  data: Map<string, LiveCountData>,
  analyticsTime: AnalyticsTime
) {
  // Fetch the list of spaces we're updating
  let spaces = await fetchAllObjects<CoreSpace>(client, '/spaces');
  if (spaceIds) {
    spaces = spaces.filter((space) => spaceIds.includes(space.id));
  }

  // Load all initial space data
  await Promise.all(
    spaces.map(async (space) => {
      // Get new data
      const newData = await getSpaceData(client, space, doorwayId, keepEventsFor, analyticsTime);
      const now = moment.utc().valueOf();

      // Merge all existing events less than two minutes old and sort
      const existingEvents = Array.from(data.get(space.id)?.recentEvents?.entries() || []);
      const newEvents = existingEvents
        .filter(([_, event]) => now - moment.utc(event.timestamp).valueOf() < 120000)
        .concat(Array.from(newData.recentEvents.entries()))
        .sort(([_, a], [__, b]) => moment.utc(a.timestamp).valueOf() - moment.utc(b.timestamp).valueOf());

      // Set the new data for the space
      newData.recentEvents = new Map(newEvents);
      data.set(space.id, newData);
    })
  );

  // If scoped to a doorway, "current count" is always invalid
  if (doorwayId) {
    return Promise.all(
      spaces.map(async (space) => {
        const spaceData = data.get(space.id);
        if (spaceData) {
          spaceData.currentCount = -1;
        }
      })
    );
    // Load "current count" for each space
  } else {
    return Promise.all(
      spaces.map(async (space) => {
        const spaceData = data.get(space.id);
        if (spaceData) {
          // Get space count as of one minute ago
          const oneMinuteAgo = moment.utc().subtract(1, 'minute');
          const count = await getSpaceCount(client, space, oneMinuteAgo);

          // Count all events newer than one minute ago
          spaceData.currentCount = Array.from(spaceData.recentEvents.values())
            .filter((event) => oneMinuteAgo.valueOf() < moment.utc(event.timestamp).valueOf())
            .reduce((acc, next) => {
              if (next.type === 'count') {
                acc = Math.max(acc + next.countChange, 0);
              } else if (next.type === 'count_reset') {
                acc = next.count;
              }
              return acc;
            }, count);
        }
      })
    );
  }
}

export function getLiveCountProvider(): LiveCountProvider {
  // Permanent references exposed in the LiveCountProvider interface
  let status = LiveCountStatus.CLOSED;
  let lastUpdate = 0;
  let data = new Map<string, LiveCountData>();
  let socket = null as WebSocket | null;

  // More permanent things, not exposed in the LiveCountProvider interface
  let pingInterval = null as NodeJS.Timeout | null;
  let shouldReconnect = true;
  let connectionAttempt = 0;

  // Aliases for the items passed to .connect()
  let _onChange: LiveCountChangeHandler;
  let _client: AxiosInstance;
  let _spaceIds: Array<string> | null;
  let _doorwayId: string | null;
  let _pollEvery: number | null;
  let _keepEventsFor: number | null;

  // When the page transitions visibility, connect or disconnect the event source
  // This prevents pushed events from piling up and crashing the page when not rendered
  let cancelVisibilityChangeListener: () => void = () => undefined;
  try {
    cancelVisibilityChangeListener = handleVisibilityChange((hidden) => {
      if (!hidden && status !== LiveCountStatus.CONNECTED) {
        connect(_onChange, _client, _spaceIds, _doorwayId, _pollEvery, _keepEventsFor);
      } else if (hidden && status !== LiveCountStatus.CLOSED) {
        disconnect();
      }
    });
  } catch {}

  /**
   * @param onChange Will be called every time there is new data
   * @param client A pre-authenticated Axios client, to use for making API calls
   * @param spaceIds List of Space IDs to filter by, if desired
   * @param pollEvery Number of milliseconds to poll every (default: 5 minutes)
   * @param keepEventsFor Number of milliseconds to keep events (default: since last "daily" reset)
   */
  async function connect(
    onChange: LiveCountChangeHandler,
    client: AxiosInstance,
    spaceIds: Array<string> | null = null,
    doorwayId: string | null = null,
    pollEvery: number | null = 300000,
    keepEventsFor: number | null = 300000,
    analyticsTime: AnalyticsTime = AnalyticsTime.TODAY
  ) {
    _onChange = onChange;
    _client = client;
    _spaceIds = spaceIds;
    _doorwayId = doorwayId;
    _pollEvery = pollEvery;
    _keepEventsFor = keepEventsFor;

    // Interval handle for polling
    let pollInterval: NodeJS.Timeout | null = null;

    // If connected already, close the connection before connecting again.
    if (status === LiveCountStatus.CONNECTED) {
      console.warn('... SOCKET IS ALREADY CONNECTED');
      return;
    }

    // Ensure that only one connection can occur at a time.
    if ([LiveCountStatus.WAITING_FOR_SOCKET_URL, LiveCountStatus.CONNECTING].includes(status)) {
      console.warn('... SOCKET CONNECTION ALREADY IN PROGRESS');
      return;
    }

    // Ensure we have an API token ready to go
    if (client && !client.defaults.headers.common['Authorization']) {
      console.error('... NO TOKEN SET, NOT CONNECTING TO SOCKET.');
      return;
    }

    // If including a doorway id, we can only have one space
    if (spaceIds?.length !== 1 && doorwayId) {
      console.warn('... CANNOT SCOPE TO DOORWAY ID UNLESS EXACTLY ONE SPACE IS PROVIDED');
      return;
    }

    // This change handler creates a new Map on each change, to be sort of redux-y
    const handleChange = (payload: LiveCountPayload | null) => {
      lastUpdate = moment.utc().valueOf();
      onChange(status, new Map<string, LiveCountData>(data), payload);
    };

    // Start the socket url connection process
    shouldReconnect = true;
    status = LiveCountStatus.WAITING_FOR_SOCKET_URL;
    handleChange(null);

    try {
      const socketsResponse = await client.post('/sockets?types=count,count_reset,display_refresh');

      if (socketsResponse.data?.url) {
        socket = new WebSocket(socketsResponse.data?.url);
        status = LiveCountStatus.CONNECTING;
        handleChange(null);

        socket.onopen = async () => {
          status = LiveCountStatus.CONNECTED;
          handleChange(null);

          // When a successful connection occurs, reset the iteration count back to zero so that the
          // backoff is reset.
          connectionAttempt = 0;

          // Every once in a while, send a message to the server to keep the websocket message alive
          pingInterval = setInterval(() => {
            socket?.send('"ping"');
          }, WEBSOCKET_PING_MESSAGE_INTERVAL_IN_MILLISECONDS);

          // Poll every [default 60s] for latest events and resets, starting immediately
          if (pollEvery) {
            pollInterval = setInterval(async () => {
              await pollForData(client, spaceIds, doorwayId, keepEventsFor, data, analyticsTime);
              handleChange(null);
            }, pollEvery);
          }
          await pollForData(client, spaceIds, doorwayId, keepEventsFor, data, analyticsTime);
          handleChange(null);
        };

        // Handle "count deltas" and "count resets", and always emit an update
        socket.onmessage = async (e) => {
          const payload = JSON.parse(e.data).payload as LiveCountPayload;

          // Get existing space data
          let spaceData: LiveCountData | undefined,
            epochTime = 0;
          if (payload.type === 'count' || payload.type === 'count_reset') {
            spaceData = data.get(payload.space_id);
            epochTime = moment.utc(payload.timestamp).valueOf();
          }

          // Merge in a new "count" (count delta / Core API "Event")
          if (spaceData && payload.type === 'count' && (!doorwayId || payload.doorway_id === doorwayId)) {
            // Save the new count (delta)
            const newCount = Math.max(spaceData.currentCount + payload.direction, 0);
            spaceData.recentEvents.set(epochTime.toString(), {
              type: 'count',
              timestamp: payload.timestamp,
              count: newCount,
              countChange: payload.direction,
              doorwayId: payload.doorway_id,
            });

            // Update statistics
            if (analyticsTime === AnalyticsTime.TODAY) {
              spaceData.todayAnalytics.max = Math.max(spaceData.currentCount, spaceData.todayAnalytics.max);
              if (payload.direction === 1) {
                spaceData.todayAnalytics.entrances += 1;
              }
              if (payload.direction === -1) {
                spaceData.todayAnalytics.exits += 1;
              }
            }

            spaceData.analytics.max = Math.max(spaceData.currentCount, spaceData.analytics.max);
            if (payload.direction === 1) {
              spaceData.analytics.entrances += 1;
            }
            if (payload.direction === -1) {
              spaceData.analytics.exits += 1;
            }

            // Update current count if newer than the latest reset, or poll if out-of-order
            if (epochTime >= spaceData.latestReset) {
              spaceData.currentCount = newCount;
            } else {
              await pollForData(client, spaceIds, doorwayId, keepEventsFor, data, analyticsTime);
              handleChange(null);
            }

            // Set the latest delta
            spaceData.latestDelta = Math.max(spaceData.latestDelta, epochTime);

            // Merge in a new "count_reset" (Core API "Reset")
          } else if (spaceData && payload.type === 'count_reset') {
            // Save the new count reset
            spaceData.recentEvents.set(epochTime.toString(), {
              type: 'count_reset',
              timestamp: payload.timestamp,
              count: payload.count,
            });

            // Update statistics
            if (analyticsTime === AnalyticsTime.TODAY) {
              spaceData.todayAnalytics.max = Math.max(spaceData.currentCount, spaceData.todayAnalytics.max);
            }

            spaceData.analytics.max = Math.max(spaceData.currentCount, spaceData.analytics.max);

            // Update current count if newer than every other update, or poll if out-of-order
            if (epochTime >= spaceData.latestReset && epochTime >= spaceData.latestDelta) {
              spaceData.currentCount = payload.count;
            } else {
              await pollForData(client, spaceIds, doorwayId, keepEventsFor, data, analyticsTime);
              handleChange(null);
            }

            // Set the latest reset
            spaceData.latestReset = Math.max(spaceData.latestReset, epochTime);
          }

          // If scoped to doorway, any "current count" is always invalid
          if (spaceData && doorwayId) {
            spaceData.currentCount = -1;
          }

          // Call the callback on every update
          handleChange(payload);
        };

        // When the connection disconnects, reconnect after a delay.
        socket.onclose = () => {
          socket = null;
          status = LiveCountStatus.CLOSED;
          handleChange(null);

          // Clear the interval that sends a ping to the sockets server if it is open.
          if (pingInterval) {
            clearInterval(pingInterval);
          }

          // Clear the poll refresh interval if it is open.
          if (pollInterval) {
            clearInterval(pollInterval);
          }

          // If we shouldn't reconnect (disconnect was intentional) then stop here
          if (!shouldReconnect) {
            shouldReconnect = true;
            return;
          }

          // Otherwise, try to reconnect w/ an exponential backoff
          connectionAttempt += 1;
          setTimeout(
            () => connect(_onChange, _client, _spaceIds, _doorwayId, _pollEvery, _keepEventsFor),
            Math.min(Math.pow(connectionAttempt, 2) * 500, 30000)
          );
        };

        // this.emit('fetchedUrl');
      }
    } catch (err) {
      // An error occured while connecting, so try to reconnect.
      status = LiveCountStatus.ERROR;
      handleChange(null);

      // Queue up the next attempt to reconnect to the socket server.
      connectionAttempt += 1;
      setTimeout(
        () => connect(_onChange, _client, _spaceIds, _doorwayId, _pollEvery, _keepEventsFor),
        Math.min(Math.pow(connectionAttempt, 2) * 500, 30000)
      );
    }
  }

  async function disconnect() {
    try {
      cancelVisibilityChangeListener();
    } finally {
      // Tell the socket not to reconnect
      shouldReconnect = false;
      try {
        socket?.close();
      } catch {
        // Reload page, since we have no idea what happened in DASH-974
        // And we don't want to make Safe Displays non-functional
        await asyncSleep(1000);
        window.location.reload();
      }

      // Reset the other permanent values
      status = LiveCountStatus.CLOSED;
      lastUpdate = 0;
      data = new Map<string, LiveCountData>();
      pingInterval = null;
      connectionAttempt = 0;

      // Wait for the socket to be closed
      while (socket !== null) {
        await asyncSleep(1000);
      }
    }
  }

  // Return provider interface
  return {
    get status() {
      return status;
    },
    get lastUpdate() {
      return lastUpdate;
    },
    get data() {
      return data;
    },
    get socket() {
      return socket;
    },
    connect,
    disconnect,
  };
}
